/**
 * Location Service
 * Handles geolocation functionality and location-based restrictions
 */

/** The error a device with no geolocation API produces. */
const UNSUPPORTED = 'UNSUPPORTED';

const unsupportedError = () => {
  const error = new Error('Geolocation is not supported by this browser');
  error.code = UNSUPPORTED;
  return error;
};

/**
 * Get the best available location using different methods.
 *
 * A denied geolocation permission (code 1) is a decision, not a transient
 * failure — it is never followed by an IP or default-location fallback.
 * Callers must treat a `PERMISSION_DENIED`-coded error as terminal: no
 * location to publish, no location to query with.
 *
 * A device with no geolocation API at all is treated the same way (Q-20
 * review L5). Nobody there was asked for location, so nobody allowed it, and
 * the Privacy Policy says the IP lookup to ipapi.co never happens without that
 * permission. The error keeps `unsupported: true` for a caller that wants to
 * word it differently.
 *
 * The IP lookup runs ONLY after a timeout (code 3): the device is allowed to
 * locate itself and could not do it in time, which is the one case the Privacy
 * Policy describes. Every other failure skips it and goes straight to the
 * local default point. That includes code 2 (POSITION_UNAVAILABLE), which is
 * what Android reports when the phone's Location switch is off: the user has
 * switched location off, and the policy says we never send their IP address to
 * ipapi.co then. The default point is never published or queried with
 * (locationPolicy.js), so the map shows its "Location is off" card, as before.
 * @returns {Promise<Object>} Location object with coords
 */
export const getBestAvailableLocation = async () => {
  try {
    // Try to get precise location from browser geolocation API
    const preciseLocation = await getCurrentPosition();
    return preciseLocation;
  } catch (error) {
    if (error?.code === 1 || error?.code === UNSUPPORTED) {
      const denied = new Error(error.message || 'Location permission denied');
      denied.code = 'PERMISSION_DENIED';
      if (error.code === UNSUPPORTED) denied.unsupported = true;
      throw denied;
    }

    if (error?.code === 3) {
      console.warn('Precise geolocation timed out, trying IP-based location:', error);
      try {
        // Fallback to IP-based geolocation
        const ipLocation = await getLocationByIP();
        return ipLocation;
      } catch (ipError) {
        console.error('IP geolocation failed:', ipError);
      }
    } else {
      console.warn('Precise geolocation failed; not asking for an IP estimate:', error);
    }

    // Return default location (Tel Aviv, Israel)
    return {
      coords: {
        latitude: 32.0853,
        longitude: 34.7818,
        accuracy: 5000
      },
      timestamp: Date.now(),
      source: 'default',
      country: 'Israel'
    };
  }
};

const positionFromGeolocation = (position) => ({
  coords: position.coords,
  timestamp: position.timestamp,
  source: 'browser',
});

/**
 * Single getCurrentPosition attempt with given options
 */
const getCurrentPositionOnce = (options) =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(unsupportedError());
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(positionFromGeolocation(pos)),
      reject,
      options
    );
  });

/**
 * Get current position: try GPS (long timeout), then Wi‑Fi/cell (faster, works better indoors).
 * @returns {Promise<Object>} Location object
 */
export const getCurrentPosition = async () => {
  if (!navigator.geolocation) {
    throw unsupportedError();
  }
  try {
    return await getCurrentPositionOnce({
      enableHighAccuracy: true,
      timeout: 45000,
      maximumAge: 0,
    });
  } catch (err) {
    // 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT — retry with low accuracy (often succeeds indoors)
    if (err?.code === 2 || err?.code === 3) {
      return getCurrentPositionOnce({
        enableHighAccuracy: false,
        timeout: 25000,
        maximumAge: 120000,
      });
    }
    throw err;
  }
};

/**
 * A single, FRESH fix for the trade meetup check-in.
 *
 * Deliberately not `getCurrentPosition` and deliberately not the map's cached or
 * privacy-jittered position: presence has to be evidence about right now, and a
 * fuzzed or minutes-old coordinate would either fail a real meet-up or "verify"
 * one that never happened. `maximumAge: 0` forces a new fix, and there is NO
 * low-accuracy retry — a rough fix is precisely what the server rejects, and the
 * in-person code exists for that case.
 *
 * Returns { lat, lng, accuracyM, capturedAt } ready to POST.
 */
export const getFreshPosition = async ({ timeout = 20000 } = {}) => {
  if (!navigator.geolocation) {
    throw new Error('Geolocation is not supported by this browser');
  }
  const position = await getCurrentPositionOnce({
    enableHighAccuracy: true,
    timeout,
    maximumAge: 0,
  });
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracyM: position.coords.accuracy,
    capturedAt: new Date(position.timestamp || Date.now()).toISOString(),
  };
};

/**
 * Watch user's position for real-time updates
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 * @returns {number} Watch ID to clear the watch later
 */
export const watchUserLocation = (onSuccess, onError) => {
  if (!navigator.geolocation) {
    onError(new Error('Geolocation is not supported by this browser'));
    return null;
  }
  
  // Low accuracy + long timeout: high accuracy causes frequent TIMEOUT (code 3) on mobile indoors.
  return navigator.geolocation.watchPosition(
    (position) => {
      onSuccess(positionFromGeolocation(position));
    },
    (error) => {
      onError(error);
    },
    {
      enableHighAccuracy: false,
      timeout: 60000,
      maximumAge: 120000,
    }
  );
};

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Deadline for the IP lookup. This call is the step between the browser
 * geolocation attempts and the hardcoded default in `getBestAvailableLocation`,
 * and it ran unbounded: a proxy or provider that accepts the request and then
 * stops answering held the map's loading state open indefinitely, because a
 * hang never rejects and so never reached the catch below.
 *
 * Longer than the server's own 5 s provider deadline
 * (`PROVIDER_TIMEOUT_MS`, server/geolocation/geolocationRoutes.js) so the
 * server still gets to answer with its 503 in the normal case.
 *
 * AbortController rather than `AbortSignal.timeout`: the latter is absent from
 * older WebViews, where calling it would throw and disable IP lookup entirely.
 */
export const IP_LOOKUP_TIMEOUT_MS = 8000;

/**
 * Get location based on IP address via server proxy (avoids CORS and client rate limits)
 * @returns {Promise<Object>} Location object
 */
export const getLocationByIP = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), IP_LOOKUP_TIMEOUT_MS);
  try {
    // The proxy now requires a session (#67). This runs outside the axios
    // instance that sets the header globally, so attach it explicitly.
    const token = localStorage.getItem('token');
    const response = await fetch(`${apiUrl}/geolocation/ip`, {
      headers: token ? { 'x-auth-token': token } : {},
      signal: controller.signal,
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || 'IP geolocation failed');
    }

    return {
      coords: {
        latitude: data.latitude,
        longitude: data.longitude,
        accuracy: 10000 // IP geolocation is less accurate
      },
      timestamp: Date.now(),
      source: 'ip',
      country: data.country,
      countryCode: data.countryCode,
      city: data.city,
      region: data.region
    };
  } catch (error) {
    // An abort lands here too, and rethrowing puts it on the existing failure
    // path: `getBestAvailableLocation` catches it and returns the default.
    console.error('IP geolocation API error:', error);
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Calculate distance between two points in meters
 * @param {Object} point1 - First point with lat/lng
 * @param {Object} point2 - Second point with lat/lng
 * @returns {number} Distance in meters
 */
export const calculateDistance = (point1, point2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = point1.latitude * Math.PI / 180;
  const φ2 = point2.latitude * Math.PI / 180;
  const Δφ = (point2.latitude - point1.latitude) * Math.PI / 180;
  const Δλ = (point2.longitude - point1.longitude) * Math.PI / 180;
  
  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  
  return R * c;
};


/**
 * Distance step and floor for anything shown to another person. Same discipline
 * as the match deck's `formatApproxDistance` (map/matchRanking.js), and for the
 * same reason: at metre granularity with no floor, a collector standing where
 * you stand renders "0m" — a doorstep stated outright. Rounding to 50 m and
 * clamping to the first step keeps the chip useful while publishing no
 * precision beyond the jittered pin the map already draws.
 */
const DISTANCE_STEP_METRES = 50;
const MIN_DISTANCE_METRES = 50;

/**
 * Format distance in meters to a human-readable string
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted distance
 */
export const formatDistance = (meters) => {
  if (meters < 1000) {
    const stepped = Math.max(
      Math.round(meters / DISTANCE_STEP_METRES) * DISTANCE_STEP_METRES,
      MIN_DISTANCE_METRES
    );
    // 975–999 m rounds up over the kilometre line; say "1.0km", not "1000m".
    if (stepped < 1000) return `${stepped}m`;
    return `${(stepped / 1000).toFixed(1)}km`;
  } else if (meters < 10000) {
    return `${(meters / 1000).toFixed(1)}km`;
  } else {
    return `${Math.round(meters / 1000)}km`;
  }
};

/**
 * Generate a random point near a given location
 * @param {Object} center - Center point with coords
 * @param {number} radiusInMeters - Radius in meters
 * @returns {Array} [longitude, latitude]
 */
export const getRandomPointNear = (center, radiusInMeters = 5000) => {
  // Earth's radius in meters
  const earthRadius = 6371000;
  
  // Convert radius from meters to degrees
  const radiusInDegrees = radiusInMeters / earthRadius * (180 / Math.PI);
  
  // Get coordinates from center
  let lat, lng;
  
  if (center.coords) {
    // If center is a geolocation position
    lat = center.coords.latitude;
    lng = center.coords.longitude;
  } else if (Array.isArray(center.coordinates)) {
    // If center is a GeoJSON-like object with coordinates array [lng, lat]
    lng = center.coordinates[0];
    lat = center.coordinates[1];
  } else if (center.lat !== undefined && center.lng !== undefined) {
    // If center has lat/lng properties
    lat = center.lat;
    lng = center.lng;
  } else if (center.latitude !== undefined && center.longitude !== undefined) {
    // If center has latitude/longitude properties
    lat = center.latitude;
    lng = center.longitude;
  } else {
    // Default to Tel Aviv
    lat = 32.0853;
    lng = 34.7818;
  }
  
  // Generate random angle in radians
  const angle = Math.random() * 2 * Math.PI;
  
  // Generate random radius (not uniform, but that's fine for this purpose)
  const randomRadius = Math.random() * radiusInDegrees;
  
  // Calculate offsets
  const latOffset = randomRadius * Math.sin(angle);
  const lngOffset = randomRadius * Math.cos(angle) / Math.cos(lat * Math.PI / 180);
  
  // Calculate new coordinates
  const newLat = lat + latOffset;
  const newLng = lng + lngOffset;
  
  return [newLng, newLat];
};

// Create a named variable for the export
const LocationService = {
  getBestAvailableLocation,
  getCurrentPosition,
  watchUserLocation,
  getLocationByIP,
  IP_LOOKUP_TIMEOUT_MS,
  calculateDistance,
  formatDistance,
  getRandomPointNear
};

export default LocationService;

